import { db } from '../firebase';
import {
  collection,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
  getDoc,
} from 'firebase/firestore';
import { Region, FairStatus, Fair } from '../types/fair.types';

//Interface for users who are admins in a region
interface AdminUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  userType: string;
  regionId: string;
}

//Interface for updating regions
interface RegionUpdate {
  name?: string;
  location?: string;
  state?: string;
  status?: string;
  maxAmountOfFairs?: number;
  schools?: string[];
  regionDirectorId?: string;
}

export interface Judge {
  judgeId: string;
  judgeCheckedIn: boolean;
}

//Function to create a new region
export const createFair = async (
  name: string,
  addressLine: string,
  addressCity: string,
  addressState: string,
  addressZip: string,
  registrationStart: Date,
  registrationEnd: Date,
  startTime: Timestamp,
  regionId: string
): Promise<string> => {
  //Create the fair data object and set the initial status to 'active' and notification to be empty
  const fairData = {
    name,
    addressLine,
    addressState,
    addressCity,
    addressZip,
    registrationStart,
    registrationEnd,
    startTime,
    notifications: [],
    status: 'active' as FairStatus,
    regionId,
    judgeIds: [],
  };
  try {
    //Add the region data to the 'fairs' collection in Firestore
    const fairRef = await addDoc(collection(db, 'fairs'), fairData);
    const fair = {
      id: fairRef.id,
      ...fairData,
    };

    //return fair ID if needed
    return fair.id;
  } catch (error) {
    console.error('Error creating fair:', error);
    throw error;
  }
};

//Function to update a region
export const updateRegion = async (
  regionId: string,
  updatedFields: RegionUpdate
): Promise<void> => {
  try {
    //Find the document reference for the region to be updated in the 'regions' collection
    const regionRef = doc(db, 'regions', regionId);
    //Update the document with the new data
    await updateDoc(regionRef, { ...updatedFields });
    console.log(`Region with ID ${regionId} updated successfully.`);
  } catch (error) {
    console.error(`Error updating region with ID ${regionId}:`, error);
    throw error;
  }
};

//Function to get all fairs in a region
export const getFairs = async (regionId: string): Promise<Fair[]> => {
  try {
    //Get a reference to all fairs in the 'fairs' collection
    const fairsRef = collection(db, 'fairs');
    //Create a query to find fairs with the specified region ID
    const q = query(fairsRef, where('regionId', '==', regionId));
    //Execute the query and get the results
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Fair[];
  } catch (error) {
    console.error('Error fetching regions:', error);
    throw error;
  }
};

//Function to get a single region by its ID
export const getRegionById = async (
  regionId: string
): Promise<Region | undefined> => {
  try {
    //Get all regions from the 'regions' collection
    const regionsSnapshot = await getDocs(collection(db, 'regions'));
    //Map the documents to an array of regions
    const allRegions = regionsSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Region[];

    //Find the region with the matching ID
    const region = allRegions.find((region) => region.id === regionId);
    //return the region if found, otherwise return undefined
    return region;
  } catch (error) {
    console.error('Error fetching region by ID:', error);
    throw error;
  }
};

//Function to get all 'active' regions
export const getActiveRegions = async (): Promise<Region[]> => {
  try {
    //Get all regions from the 'regions' collection
    const regionsSnapshot = await getDocs(collection(db, 'regions'));
    //Map the documents to an array of regions
    const allRegions = regionsSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Region[];

    //Filter the regions to only include those with status "active"
    const activeRegions = allRegions.filter(
      (region) => region.status === 'active'
    );

    //return the active regions
    return activeRegions;
  } catch (error) {
    console.error('Error fetching active regions:', error);
    throw error;
  }
};

//Function to delete a fair by its ID
export const deleteFair = async (fairId: string): Promise<void> => {
  try {
    //Find the document reference for the fair to be deleted in the 'fairs' collection by it's ID
    const fairRef = doc(db, 'fairs', fairId);
    //Delete the document from the database
    await deleteDoc(fairRef);
    console.log(`Fair with ID ${fairId} deleted successfully.`);
  } catch (error) {
    console.error(`Error deleting fair with ID ${fairId}:`, error);
    throw error;
  }
};

//Function to get all admins in a specific region by its ID
export const getAdminsInRegion = async (
  regionId: string
): Promise<AdminUser[]> => {
  try {
    //Get a reference to all users in the 'users' collection
    const usersRef = collection(db, 'users');
    //Create a query to find users who are admins and belong to the specified region
    const q = query(
      usersRef,
      where('userType', '==', 'admin'),
      where('regionId', '==', regionId)
    );

    //Execute the query and get the results
    const querySnapshot = await getDocs(q);
    //Map the results to an array of AdminUser objects
    const adminsInRegion: AdminUser[] = [];

    //Create an array of AdminUser objects from the query results
    querySnapshot.forEach((doc) => {
      const userData = doc.data();
      adminsInRegion.push({
        id: doc.id,
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        userType: userData.userType || '',
        regionId: userData.regionId || '',
      } as AdminUser);
    });

    //return the array of AdminUser objects
    return adminsInRegion;
  } catch (error) {
    console.error(`Error fetching admins in region ${regionId}:`, error);
    throw error;
  }
};

export const updateFair = async (
  fairId: string,
  name: string,
  addressLine: string,
  addressCity: string,
  addressState: string,
  addressZip: string,
  registrationStart: Date,
  registrationEnd: Date,
  startTime: Timestamp
) => {
  try {
    // Get reference to the fair document
    const fairRef = doc(db, 'fairs', fairId);

    // Create the updated fair data object
    const fairData = {
      name,
      addressLine,
      addressCity: addressCity,
      addressState: addressState,
      addressZip: addressZip,
      registrationStart,
      registrationEnd,
      startTime,
    };

    // Update the document
    await updateDoc(fairRef, fairData);

    console.log('Fair updated successfully with ID: ', fairId);
    return fairId;
  } catch (error) {
    console.error('Error updating fair: ', error);
    throw error;
  }
};

export const startStopFair = async (
  fairId: string,
  startStopDecision: string
) => {
  let fairStatus: FairStatus = 'active'; // Default status
  try {
    if (startStopDecision === 'Start') {
      fairStatus = 'inProgress' as FairStatus;
    } else if (startStopDecision === 'Stop') {
      fairStatus = 'completed' as FairStatus;
    }

    // Get reference to the fair document
    const fairRef = doc(db, 'fairs', fairId);

    // Create the updated fair data object
    const fairData = {
      status: fairStatus,
    };

    // Update the document
    await updateDoc(fairRef, fairData);

    console.log('Fair status updated successfully with ID: ', fairId);
    return fairId;
  } catch (error) {
    console.error('Error updating fair status: ', error);
    throw error;
  }
};

export const countFairsInRegion = async (regionId: string): Promise<number> => {
  try {
    console.log('Counting fairs in region:', regionId);
    // Get a reference to the fairs collection
    const fairsRef = collection(db, 'fairs');
    // Create a query to count the number of fairs in the specified region
    const q = query(fairsRef, where('regionId', '==', regionId));
    // Execute the query and get the results
    const querySnapshot = await getDocs(q);
    // Return the count of fairs
    return querySnapshot.docs.length;
  } catch (error) {
    console.error('Error counting fairs in region:', error);
    throw error;
  }
};

export const getActiveFairsInRegion = async (
  regionId: string
): Promise<Fair[]> => {
  try {
    // Get a reference to the fairs collection
    const fairsRef = collection(db, 'fairs');
    // Create a query to find fairs with the specified region ID
    const q = query(
      fairsRef,
      where('regionId', '==', regionId),
      where('status', '==', 'active')
    );
    // Execute the query and get the results
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Fair[];
  } catch (error) {
    console.error('Error fetching fairs:', error);
    throw error;
  }
};

export const addJudgeToFair = async (
  fairId: string,
  judgeId: string
): Promise<void> => {
  try {
    // Get the current fair data
    const fairRef = doc(db, 'fairs', fairId);
    const fairDoc = await getDoc(fairRef);

    if (!fairDoc.exists()) {
      throw new Error(`Fair with ID ${fairId} not found`);
    }

    const fairData = fairDoc.data();
    const currentJudges: Judge[] = fairData.judges || [];

    // Check if judge already exists
    if (currentJudges.some((judge) => judge.judgeId === judgeId)) {
      console.log(`Judge ${judgeId} already assigned to fair`);
      return;
    }

    // Add the new judge with default checked-in status as false
    const updatedJudges = [
      ...currentJudges,
      { judgeId, judgeCheckedIn: false },
    ];

    // Update the document
    await updateDoc(fairRef, { judges: updatedJudges });

    console.log(`Judge ${judgeId} added to fair ${fairId}`);
  } catch (error) {
    console.error('Error adding judge to fair: ', error);
    throw error;
  }
};

// Helper function to remove a judge
export const removeJudgeFromFair = async (
  fairId: string,
  judgeId: string
): Promise<void> => {
  try {
    // Get the current fair data
    const fairRef = doc(db, 'fairs', fairId);
    const fairDoc = await getDoc(fairRef);

    if (!fairDoc.exists()) {
      throw new Error(`Fair with ID ${fairId} not found`);
    }

    const fairData = fairDoc.data();
    const currentJudges: Judge[] = fairData.judges || [];

    // Filter out the judge to remove
    const updatedJudges = currentJudges.filter(
      (judge) => judge.judgeId !== judgeId
    );

    // If no changes were made, judge wasn't in the list
    if (updatedJudges.length === currentJudges.length) {
      console.log(`Judge ${judgeId} not found in fair ${fairId}`);
      return;
    }

    // Update the document
    await updateDoc(fairRef, { judges: updatedJudges });

    console.log(`Judge ${judgeId} removed from fair ${fairId}`);
  } catch (error) {
    console.error('Error removing judge from fair: ', error);
    throw error;
  }
};

// Helper function to update a judge's check-in status
export const updateJudgeCheckInStatus = async (
  fairId: string,
  judgeId: string,
  checkedIn: boolean
): Promise<void> => {
  try {
    // Get the current fair data
    const fairRef = doc(db, 'fairs', fairId);
    const fairDoc = await getDoc(fairRef);

    if (!fairDoc.exists()) {
      throw new Error(`Fair with ID ${fairId} not found`);
    }

    const fairData = fairDoc.data();
    const currentJudges: Judge[] = fairData.judges || [];

    // Find the judge and update their check-in status
    const updatedJudges = currentJudges.map((judge) =>
      judge.judgeId === judgeId
        ? { ...judge, judgeCheckedIn: checkedIn }
        : judge
    );

    // If no changes were made, judge wasn't in the list
    if (JSON.stringify(updatedJudges) === JSON.stringify(currentJudges)) {
      console.log(`Judge ${judgeId} not found in fair ${fairId}`);
      return;
    }

    // Update the document
    await updateDoc(fairRef, { judges: updatedJudges });

    console.log(`Judge ${judgeId} check-in status updated to ${checkedIn}`);
  } catch (error) {
    console.error('Error updating judge check-in status: ', error);
    throw error;
  }
};

export const getRegistrableFairsInRegion = async (
  regionId: string
): Promise<Fair[]> => {
  try {
    // Get a reference to the fairs collection
    const fairsRef = collection(db, 'fairs');

    console.log('Fetching registrable fairs for region:', regionId);
    // Create a simpler query with just equality conditions
    const q = query(
      fairsRef,
      where('regionId', '==', regionId),
      where('status', '==', 'active')
    );

    console.log('Query:', q);

    // Execute the query and get the results
    const querySnapshot = await getDocs(q);
    const currentDate = new Date();

    // Filter results programmatically for both date conditions
    return querySnapshot.docs
      .map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
        } as Fair;
      })
      .filter((fair) => {
        const registrationStart =
          fair.registrationStart instanceof Timestamp
            ? fair.registrationStart.toDate()
            : fair.registrationStart;
        const registrationEnd =
          fair.registrationEnd instanceof Timestamp
            ? fair.registrationEnd.toDate()
            : fair.registrationEnd;

        // Check if current date is within registration period
        return (
          registrationStart <= currentDate && registrationEnd >= currentDate
        );
      });
  } catch (error) {
    console.error('Error fetching fairs:', error);
    throw error;
  }
};

export const getJudgeFairs = async (judgeId: string): Promise<Fair[]> => {
  try {
    // Get a reference to the fairs collection
    const fairsRef = collection(db, 'fairs');

    // The issue is here: we need to query differently depending on your data structure
    const querySnapshot = await getDocs(fairsRef);

    // Filter results programmatically to find fairs where the judge exists
    return querySnapshot.docs
      .filter((doc) => {
        const data = doc.data();
        // Check if the judges array contains an object with the matching judgeId
        return (
          data.judges &&
          Array.isArray(data.judges) &&
          data.judges.some((judge) => judge.judgeId === judgeId)
        );
      })
      .map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
        } as Fair;
      });
  } catch (error) {
    console.error('Error fetching fairs:', error);
    throw error;
  }
};

/**
 * Sets the active rubric for a specific fair
 * @param fairId - ID of the fair to update
 * @param rubricId - ID of the rubric to set as active
 */
export const setFairActiveRubric = async (
  fairId: string,
  rubricId: string
): Promise<void> => {
  try {
    const fairRef = doc(db, 'fairs', fairId);
    await updateDoc(fairRef, {
      activeRubricId: rubricId,
      updatedAt: new Date(), // Add timestamp for tracking
    });
    console.log(`Fair ${fairId} active rubric set to ${rubricId}`);
  } catch (error) {
    console.error('Error setting fair active rubric:', error);
    throw error;
  }
};

/**
 * Gets the active rubric ID for a specific fair
 * @param fairId - ID of the fair to check
 * @returns Promise<string | null> - The active rubric ID or null if none set
 */
export const getFairActiveRubric = async (
  fairId: string
): Promise<string | null> => {
  try {
    const fairRef = doc(db, 'fairs', fairId);
    const fairDoc = await getDoc(fairRef);

    if (!fairDoc.exists()) {
      throw new Error(`Fair with ID ${fairId} not found`);
    }

    const fairData = fairDoc.data();
    return fairData.activeRubricId || null;
  } catch (error) {
    console.error('Error getting fair active rubric:', error);
    throw error;
  }
};
